import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  QuestionInput,
  QuestionType,
  TemplateSummary,
  TemplateVersionDetail,
} from '@blesk/contracts';
import { apiGet, apiPatch, apiPost } from '../../shared/api';
import { TEMPLATE_STATUS_LABEL } from '../../shared/labels';
import { formatInProjectTz } from '../../shared/format';
import { useHasPermission, useSession } from '../../shared/session';
import { useUrlFilter } from '../../shared/filters';
import { PermissionGate } from '../shell/PermissionGate';
import { Dialog, ErrorNote, useConfirm } from '../shell/ui';

/** §9.1, §13 ТЗ. Раздел «Шаблоны и версии опросов». */
export function SurveyTemplatesPage() {
  return (
    <PermissionGate permission="surveys.read">
      <TemplatesInner />
    </PermissionGate>
  );
}

const QUESTION_TYPE_LABEL: Record<QuestionType, string> = {
  RATING_1_5: 'Оценка 1–5',
  SINGLE_CHOICE: 'Выбор варианта',
  TEXT: 'Свободный текст',
};

function TemplatesInner() {
  const session = useSession();
  const canWrite = useHasPermission('surveys.write');
  const confirmation = useConfirm();
  const queryClient = useQueryClient();
  // `draftFrom` — конструктор открыт из опубликованной версии: черновик ещё не
  // существует и появится только по «Сохранить». Создавать его нажатием кнопки
  // нельзя: «Отмена» оставляла бы в списке версию, которую нечем удалить (§13,
  // прогон QA 22.08.2026, дефект 4).
  const [editorFor, setEditorFor] = useState<{
    versionId: string | null;
    draftFrom?: boolean;
  } | null>(null);
  const [viewVersionId, setViewVersionId] = useState<string | null>(null);
  // Отбор живёт в адресе, как во всех разделах: ссылку на список с архивными
  // можно переслать, и обновление страницы его не теряет.
  const [archived, setArchived] = useUrlFilter('archived');
  const showArchived = archived === 'true';

  const templates = useQuery({
    queryKey: ['surveys', 'templates'],
    queryFn: () => apiGet<TemplateSummary[]>('/api/surveys/templates'),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['surveys'] });

  const publish = useMutation({
    mutationFn: (versionId: string) => apiPost(`/api/surveys/versions/${versionId}/publish`),
    onSuccess: invalidate,
  });
  /** §13: анкета архивируется, а не удаляется; отказ сервера показывает `ErrorNote`. */
  const archive = useMutation({
    mutationFn: (templateId: string) => apiPost(`/api/surveys/templates/${templateId}/archive`),
    onSuccess: invalidate,
  });
  const restore = useMutation({
    mutationFn: (templateId: string) => apiPost(`/api/surveys/templates/${templateId}/restore`),
    onSuccess: invalidate,
  });

  const all = templates.data ?? [];
  // Архивные скрыты по умолчанию: обычная работа идёт с действующими анкетами
  const visible = showArchived ? all : all.filter((template) => !template.archivedAt);
  const archivedCount = all.filter((template) => template.archivedAt).length;

  return (
    <>
      <h1>Шаблоны и версии опросов</h1>
      <p className="muted">
        Опубликованная версия неизменна; правки идут в новый черновик. До 10 вопросов.
      </p>

      <div className="toolbar">
        <label>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(event) => setArchived(event.target.checked ? 'true' : '')}
          />
          Показывать архивные{archivedCount > 0 ? ` (${archivedCount})` : ''}
        </label>
        <span className="toolbar__spacer" />
        {canWrite && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setEditorFor({ versionId: null })}
          >
            Новая анкета
          </button>
        )}
      </div>
      <ErrorNote error={templates.error ?? publish.error ?? archive.error ?? restore.error} />

      {visible.map((template) => (
        <div key={template.id} className="section-block">
          <div className="section-head">
            <h2>
              {template.name}
              {template.archivedAt && (
                <span className="badge badge--state cell-mark__badge">в архиве</span>
              )}
            </h2>
            {canWrite && (
              <div className="row-actions">
                {template.archivedAt ? (
                  <button
                    type="button"
                    disabled={restore.isPending}
                    onClick={() => restore.mutate(template.id)}
                  >
                    Вернуть из архива
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={archive.isPending}
                    onClick={() =>
                      confirmation.ask({
                        title: 'Архивирование анкеты',
                        question: `Отправить анкету «${template.name}» в архив?`,
                        warning:
                          'Новые расписания по ней завести будет нельзя; отправленные опросы останутся как есть.',
                        verb: 'В архив',
                        danger: true,
                        onConfirm: () => archive.mutate(template.id),
                      })
                    }
                  >
                    В архив
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Версия</th>
                  <th>Статус</th>
                  <th>Вопросов</th>
                  <th>Опубликована</th>
                  <th>Изменена</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {template.versions.map((version) => (
                  <tr key={version.id}>
                    <td>№ {version.version}</td>
                    <td>{TEMPLATE_STATUS_LABEL[version.status]}</td>
                    <td>{version.questionCount}</td>
                    <td>{formatInProjectTz(version.publishedAt, session.defaultTimezone)}</td>
                    {/* Дата правки осмысленна только у черновика: у опубликованной версии
                        она сдвинулась бы при уходе версии в архив и читалась бы как правка
                        неизменяемой анкеты */}
                    <td>
                      {version.status === 'DRAFT'
                        ? formatInProjectTz(version.updatedAt, session.defaultTimezone)
                        : '—'}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button type="button" onClick={() => setViewVersionId(version.id)}>
                          Вопросы
                        </button>
                        {canWrite && version.status === 'DRAFT' && (
                          <>
                            <button
                              type="button"
                              onClick={() => setEditorFor({ versionId: version.id })}
                            >
                              Править
                            </button>
                            <button
                              type="button"
                              className="btn-primary"
                              disabled={publish.isPending}
                              onClick={() =>
                                confirmation.ask({
                                  title: 'Публикация версии',
                                  question: `Опубликовать версию ${version.version} анкеты «${template.name}»?`,
                                  warning:
                                    'Правило негатива зафиксируется снимком, дальнейшие правки — только новым черновиком.',
                                  verb: 'Опубликовать',
                                  onConfirm: () => publish.mutate(version.id),
                                })
                              }
                            >
                              Опубликовать
                            </button>
                          </>
                        )}
                        {/* Пока черновик существует, второй завести нельзя (§9.1):
                            кнопка, всегда отвечающая ошибкой, прячется */}
                        {canWrite &&
                          version.status === 'PUBLISHED' &&
                          !template.versions.some((v) => v.status === 'DRAFT') && (
                            <button
                              type="button"
                              onClick={() =>
                                setEditorFor({ versionId: version.id, draftFrom: true })
                              }
                            >
                              Новый черновик
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {visible.length === 0 && (
        <p className="muted">
          {all.length === 0
            ? 'Анкет пока нет.'
            : 'Действующих анкет нет — включите показ архивных.'}
        </p>
      )}

      {viewVersionId && (
        <VersionViewDialog versionId={viewVersionId} onClose={() => setViewVersionId(null)} />
      )}
      {editorFor && (
        <TemplateEditor
          versionId={editorFor.versionId}
          draftFrom={editorFor.draftFrom ?? false}
          onClose={() => setEditorFor(null)}
          onDone={invalidate}
        />
      )}
      {confirmation.dialog}
    </>
  );
}

function VersionViewDialog({ versionId, onClose }: { versionId: string; onClose: () => void }) {
  const version = useQuery({
    queryKey: ['surveys', 'version', versionId],
    queryFn: () => apiGet<TemplateVersionDetail>(`/api/surveys/versions/${versionId}`),
  });

  return (
    <Dialog
      title={
        version.data ? `${version.data.templateName} · версия ${version.data.version}` : 'Версия'
      }
      onClose={onClose}
    >
      <ErrorNote error={version.error} />
      {version.data && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>№</th>
                <th>Вопрос</th>
                <th>Тип</th>
                <th>Обязательный</th>
                <th>В правиле негатива</th>
              </tr>
            </thead>
            <tbody>
              {version.data.questions.map((question) => (
                <tr key={question.key}>
                  <td>{question.order}</td>
                  <td>
                    {question.text}
                    {question.options && (
                      <div className="muted">{question.options.join(' · ')}</div>
                    )}
                  </td>
                  <td>{QUESTION_TYPE_LABEL[question.type]}</td>
                  <td>{question.required ? 'да' : 'нет'}</td>
                  <td>{question.scored ? 'да' : 'нет'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}

interface DraftQuestion {
  key: string;
  text: string;
  type: QuestionType;
  required: boolean;
  scored: boolean;
  options: string;
}

const emptyQuestion = (index: number): DraftQuestion => ({
  key: `q_${index}`,
  text: '',
  type: 'RATING_1_5',
  required: true,
  scored: true,
  options: '',
});

/**
 * Конструктор черновика: новая анкета, правка DRAFT-версии или новый черновик
 * из опубликованной (`draftFrom`). В последнем случае до «Сохранить» на сервере
 * ничего не создаётся: конструктор заполняется содержимым опубликованной
 * версии, а черновик появляется вместе с содержимым (дефект 4 QA 22.08.2026).
 */
function TemplateEditor({
  versionId,
  draftFrom,
  onClose,
  onDone,
}: {
  versionId: string | null;
  draftFrom: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [questions, setQuestions] = useState<DraftQuestion[]>([emptyQuestion(1)]);
  const [loaded, setLoaded] = useState(versionId === null);

  const existing = useQuery({
    queryKey: ['surveys', 'version', versionId],
    queryFn: () => apiGet<TemplateVersionDetail>(`/api/surveys/versions/${versionId}`),
    enabled: versionId !== null,
  });

  if (versionId && existing.data && !loaded) {
    setName(existing.data.templateName);
    setQuestions(
      existing.data.questions.map((question) => ({
        key: question.key,
        text: question.text,
        type: question.type,
        required: question.required,
        scored: question.scored,
        options: question.options?.join(', ') ?? '',
      })),
    );
    setLoaded(true);
  }

  const save = useMutation({
    mutationFn: () => {
      const payload: QuestionInput[] = questions.map((question) => ({
        key: question.key.trim(),
        text: question.text.trim(),
        type: question.type,
        required: question.required,
        scored: question.type === 'RATING_1_5' ? question.scored : false,
        ...(question.type === 'SINGLE_CHOICE'
          ? {
              options: question.options
                .split(',')
                .map((o) => o.trim())
                .filter(Boolean),
            }
          : {}),
      }));
      if (!versionId) return apiPost('/api/surveys/templates', { name, questions: payload });
      return draftFrom
        ? apiPost(`/api/surveys/versions/${versionId}/draft`, { name, questions: payload })
        : apiPatch(`/api/surveys/versions/${versionId}`, { name, questions: payload });
    },
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  const update = (index: number, patch: Partial<DraftQuestion>) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  };

  return (
    <Dialog
      title={versionId ? (draftFrom ? 'Новый черновик' : 'Черновик анкеты') : 'Новая анкета'}
      onClose={onClose}
      confirmClose="Закрыть конструктор? Несохранённый черновик пропадёт."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <label>
          Название анкеты
          <input value={name} onChange={(e) => setName(e.target.value)} required minLength={3} />
        </label>

        {questions.map((question, index) => (
          <fieldset key={index} className="form">
            <legend className="muted">Вопрос {index + 1}</legend>
            <label>
              Ключ (латиница, стабилен между версиями)
              <input
                value={question.key}
                onChange={(e) => update(index, { key: e.target.value })}
                required
                pattern="[a-z][a-z0-9_]{1,49}"
              />
            </label>
            <label>
              Текст вопроса
              <input
                value={question.text}
                onChange={(e) => update(index, { text: e.target.value })}
                required
                minLength={3}
              />
            </label>
            <label>
              Тип
              <select
                value={question.type}
                onChange={(e) => update(index, { type: e.target.value as QuestionType })}
              >
                {Object.entries(QUESTION_TYPE_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            {question.type === 'SINGLE_CHOICE' && (
              <label>
                Варианты через запятую (2–10)
                <input
                  value={question.options}
                  onChange={(e) => update(index, { options: e.target.value })}
                  required
                />
              </label>
            )}
            <label>
              <span>
                Обязательный{' '}
                <input
                  type="checkbox"
                  checked={question.required}
                  onChange={(e) => update(index, { required: e.target.checked })}
                />
              </span>
            </label>
            {question.type === 'RATING_1_5' && (
              <label>
                <span>
                  Участвует в правиле негатива{' '}
                  <input
                    type="checkbox"
                    checked={question.scored}
                    onChange={(e) => update(index, { scored: e.target.checked })}
                  />
                </span>
              </label>
            )}
            {questions.length > 1 && (
              <button
                type="button"
                className="btn-danger"
                onClick={() => setQuestions((prev) => prev.filter((_, i) => i !== index))}
              >
                Убрать вопрос
              </button>
            )}
          </fieldset>
        ))}

        {questions.length < 10 && (
          <button
            type="button"
            onClick={() => setQuestions((prev) => [...prev, emptyQuestion(prev.length + 1)])}
          >
            Добавить вопрос
          </button>
        )}

        <ErrorNote error={save.error ?? existing.error} />
        <div className="form__actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            Сохранить черновик
          </button>
        </div>
      </form>
    </Dialog>
  );
}
